/**
 * Shared operation-feedback types and step statuses.
 * See public/docs/core/OPERATION_FEEDBACK.md and OPERATION_FEEDBACK_SPEC.
 */

/** @typedef {'toast' | 'dialog' | 'inline' | 'none'} PresentationOwner */

/** Step / readiness statuses (execution vs readiness must not be mixed). */
export const STEP_STATUS = Object.freeze({
  PENDING: "pending",
  RUNNING: "running",
  COMPLETED: "completed",
  ALREADY_APPLIED: "already_applied",
  NOT_RELEVANT: "not_relevant",
  NEEDS_FIX: "needs_fix",
  NOT_DONE: "not_done",
  FAILED: "failed",
  ROLLED_BACK: "rolled_back",
  UNCONFIRMED: "unconfirmed",
  CANCELLED: "cancelled",
});

/** Aggregate operation phase. */
export const OPERATION_PHASE = Object.freeze({
  IDLE: "idle",
  CONFIRMING: "confirming",
  RUNNING: "running",
  RESULT: "result",
  HIDDEN: "hidden",
});

/** Outcome of a mutation / step (serializable). */
export const OUTCOME = Object.freeze({
  NOT_APPLIED: "not_applied",
  APPLIED: "applied",
  PARTIAL: "partial",
  UNKNOWN: "unknown",
});

/** Error kinds for normalized frontend errors. */
export const ERROR_KIND = Object.freeze({
  VALIDATION: "validation",
  BUSINESS_RULE: "business_rule",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  NETWORK: "network",
  TIMEOUT: "timeout",
  CANCELLED: "cancelled",
  SERVER: "server",
  INTEGRATION: "integration",
  UNKNOWN: "unknown",
});

export const SIMPLE_SUCCESS_TOAST_MS = 8000;

/**
 * @param {object} [opts]
 * @param {string} [opts.operationKey]
 * @param {string|number|null} [opts.resourceId]
 * @param {string|number|null} [opts.companyId]
 * @param {string|number|null} [opts.userId]
 * @param {string} [opts.attemptId]
 */
export function createClientOperationId(opts = {}) {
  const attempt =
    opts.attemptId ||
    (typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `a-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const parts = [
    opts.operationKey || "op",
    opts.companyId != null ? String(opts.companyId) : "c?",
    opts.userId != null ? String(opts.userId) : "u?",
    opts.resourceId != null ? String(opts.resourceId) : "r?",
    attempt,
  ];
  return parts.join(":");
}

/**
 * Statuses that mean "confirmed success for this step" (not readiness).
 * @param {string} status
 */
export function isConfirmedStepSuccess(status) {
  return (
    status === STEP_STATUS.COMPLETED || status === STEP_STATUS.ALREADY_APPLIED
  );
}

/**
 * Empty errors collections must not be treated as failure.
 * @param {unknown} errors
 */
export function hasMeaningfulErrors(errors) {
  if (errors == null) return false;
  if (Array.isArray(errors)) return errors.length > 0;
  if (typeof errors === "string") return errors.trim().length > 0;
  if (typeof errors === "object") return Object.keys(errors).length > 0;
  return Boolean(errors);
}
